import { Button, Col, Drawer, Form, Input, Row, Select } from 'antd';
import { Formik } from 'formik';
import { FiCheck } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import 'draft-js/dist/Draft.css';
import type { EmailTemplateDrawerProps } from '../../EmailTemplates.types.ts';
import {
  EMAIL_TEMPLATES_FORM,
  EMAIL_TEMPLATES_FORM_SCHEMA,
  EMAIL_TEMPLATES_INITIAL_FORM_VALUES,
} from '../../EmailTemplates.constants.tsx';
import { HTMLEditor } from '../../../../components/html-editor';
import { useTranslation } from 'react-i18next';
import { getEmailTemplateValues } from '../../EmailTemplates.helpers.ts';
import { useService } from '../../EmailTemplates.hooks.ts';

export default function TemplateDrawer({
  onClose,
  open,
  onSubmit,
  emailTemplateId,
}: EmailTemplateDrawerProps) {
  const { t } = useTranslation();
  const { template } = useService({ emailTemplateId });

  const initialValues = emailTemplateId
    ? getEmailTemplateValues(template)
    : EMAIL_TEMPLATES_INITIAL_FORM_VALUES;
  return (
    <Drawer
      title={emailTemplateId ? template?.templateId : t('pages.email-templates.create')}
      placement="right"
      closable={false}
      width={500}
      onClose={onClose}
      open={open}
      extra={
        <Row justify="end" align="middle" gutter={8}>
          <span onClick={onClose} style={{ cursor: 'pointer' }}>
            <MdClose fontSize={20} />
          </span>
        </Row>
      }
      footer={
        <Row justify="end" gutter={8}>
          <Col>
            <Button size="large" onClick={onClose}>
              Cancel
            </Button>
          </Col>
          <Col>
            <Button
              size="large"
              type="primary"
              icon={<FiCheck />}
              iconPosition="end"
              htmlType="submit"
              form={EMAIL_TEMPLATES_FORM}
            >
              {t('common.confirm')}
            </Button>
          </Col>
        </Row>
      }
    >
      <Formik
        initialValues={initialValues}
        validationSchema={EMAIL_TEMPLATES_FORM_SCHEMA}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ handleSubmit, handleChange, values, errors, touched, setFieldValue }) => {
          return (
            <Form layout="vertical" onFinish={handleSubmit} id={EMAIL_TEMPLATES_FORM}>
              <Form.Item
                label={t('form.field.name')}
                validateStatus={touched.title && errors.title ? 'error' : ''}
                help={touched.title && typeof errors.title === 'string' ? errors.title : undefined}
              >
                <Input
                  name="title"
                  variant="underlined"
                  value={values.title}
                  onChange={handleChange}
                  placeholder={t('form.placeholder.type-here')}
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.subject')}
                validateStatus={touched.subject && errors.subject ? 'error' : ''}
                help={
                  touched.subject && typeof errors.subject === 'string' ? errors.subject : undefined
                }
              >
                <Input
                  name="subject"
                  variant="underlined"
                  value={values.subject}
                  onChange={handleChange}
                  placeholder={t('form.placeholder.type-here')}
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.to-address')}
                validateStatus={touched.to && errors.to ? 'error' : ''}
                help={touched.to && typeof errors.to === 'string' ? errors.to : undefined}
              >
                <Input
                  name="to"
                  variant="underlined"
                  value={values.to}
                  onChange={handleChange}
                  placeholder={t('form.placeholder.type-here')}
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.from-address')}
                validateStatus={touched.from && errors.from ? 'error' : ''}
                help={touched.from && typeof errors.from === 'string' ? errors.from : undefined}
              >
                <Input
                  name="from"
                  variant="underlined"
                  value={values.from}
                  onChange={handleChange}
                  placeholder={t('form.placeholder.type-here')}
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.body-template')}
                validateStatus={touched.body && errors.body ? 'error' : ''}
                help={touched.body && typeof errors.body === 'string' ? errors.body : undefined}
              >
                <HTMLEditor id="body" name="body" readOnly={false} />
              </Form.Item>

              <Form.Item
                label={t('form.field.is-html')}
                validateStatus={touched.isHtml && errors.isHtml ? 'error' : ''}
              >
                <Select
                  variant="underlined"
                  value={values.isHtml}
                  onChange={(e) => setFieldValue('isHtml', e)}
                  options={[
                    { value: true, label: t('common.yes') },
                    { value: false, label: t('common.no') },
                  ]}
                />
              </Form.Item>
            </Form>
          );
        }}
      </Formik>
    </Drawer>
  );
}
